import type { ViewRules } from '../engine';
import { ticksToRun } from './accumulator';

export interface LoopHandlers {
  readonly tickMs: () => number;
  readonly advance: () => void;
  readonly draw: (alpha: number) => void;
  readonly done: () => boolean;
  /** 연속 정지가 pauseAbandonMs 를 넘긴 채 재개됐다 — 뷰가 endReason='abandoned' 로 판을 닫는다 */
  readonly onPauseTooLong: () => void;
  /**
   * 정지 한 구간이 끝날 때마다 그 연속 길이(ms). 탭 숨김·가로 회전·재개 카운트다운을 구분하지 않는다 —
   * RULES §2.6·2.7 의 '일시정지 누적'(game_over 의 hiddenMs/hiddenCount)이 바로 이 값이다.
   * onPauseTooLong 보다 먼저 불려 abandoned 판의 game_over 에도 마지막 구간이 실린다.
   */
  readonly onPauseEnd?: (pausedMs: number) => void;
  /** true 면 틱을 진행하지 않고 그리기만 한다(가로 회전·재개 카운트다운). 누적 시간은 버린다. */
  readonly paused?: () => boolean;
  /** 보간 단계 수 — 페널티 중에는 alphaStepsPenalty 로 낮춘다 */
  readonly alphaSteps?: () => number;
}

export interface Loop {
  stop(): void;
}

/** 정지 시계 — 정지 중이면 시작 시각(ms), 아니면 -1. 초기 대기를 합산하지 않는 센티넬이다(RULES §2.6). */
export interface PauseClock {
  readonly since: number;
}

export const PAUSE_IDLE: PauseClock = { since: -1 };

export interface PauseStep {
  readonly clock: PauseClock;
  /** 이번 전이에서 정지가 끝났으면 그 연속 길이(ms), 아니면 -1 */
  readonly resumedAfterMs: number;
  /** resumedAfterMs > abandonMs — 경계값은 abandoned 가 아니다(기존 `away > pauseAbandonMs` 와 동일) */
  readonly abandoned: boolean;
}

/**
 * 정지 상태 전이 — 순수 함수. 정지 사유(탭 숨김 · 가로 회전 · 재개 카운트다운)가 몇 개든
 * '정지 시작 시각'은 이 시계 하나다: 사유가 하나라도 남아 있는 동안은 같은 구간이고, 전부 풀리는
 * 순간의 연속 길이로 abandon 을 판정한다. 그래서 가로로 30분 두고 세로로 돌리면 카운트다운이 끝나는
 * 프레임에 abandoned 가 되고, 탭을 15분 숨겼다가 가로로 15분 더 두는 것도 30분 한 구간으로 센다.
 */
export function stepPause(
  clock: PauseClock,
  pausedNow: boolean,
  nowMs: number,
  abandonMs: number,
): PauseStep {
  if (pausedNow) {
    return {
      clock: clock.since >= 0 ? clock : { since: nowMs },
      resumedAfterMs: -1,
      abandoned: false,
    };
  }
  if (clock.since < 0) return { clock, resumedAfterMs: -1, abandoned: false };
  // rAF 타임스탬프는 프레임 시작 시각이라 직전 visibilitychange 의 performance.now() 보다 몇 ms 앞설 수 있다
  const ms = Math.max(0, nowMs - clock.since);
  return { clock: PAUSE_IDLE, resumedAfterMs: ms, abandoned: ms > abandonMs };
}

/**
 * 고정 틱 + 렌더 보간. 게임 상태 갱신에 delta time 을 쓰지 않는다.
 * 정지(탭 숨김·가로 회전·카운트다운)에서 복귀할 때 누적 시간을 폐기해 뱀이 화면 밖에서 순간이동하지 않게 한다.
 */
export function startLoop(R: ViewRules, h: LoopHandlers): Loop {
  let raf = 0;
  let last = performance.now();
  let acc = 0;
  let hidden = document.visibilityState === 'hidden';
  let pause: PauseClock = PAUSE_IDLE;
  let stopped = false;

  // 정지 사유의 합 — 탭 숨김은 rAF 자체가 멎으므로 이벤트로 받고, 나머지는 프레임마다 콜백으로 읽는다
  const isPaused = (): boolean => hidden || h.paused?.() === true;

  /** 정지 시계를 한 번 전이시키고 정지 중인지 돌려준다. 재개 프레임에서는 누적 시간을 버린다. */
  const settle = (now: number): boolean => {
    const step = stepPause(pause, isPaused(), now, R.pauseAbandonMs);
    pause = step.clock;
    if (step.resumedAfterMs >= 0) {
      last = now;
      acc = 0;
      h.onPauseEnd?.(step.resumedAfterMs);
      if (step.abandoned) h.onPauseTooLong();
    }
    return pause.since >= 0;
  };

  const onVisibility = (): void => {
    hidden = document.visibilityState === 'hidden';
    settle(performance.now());
  };
  document.addEventListener('visibilitychange', onVisibility);

  const frame = (now: number): void => {
    if (stopped) return;
    if (settle(now)) {
      // 정지 중에는 시간을 버린다 — 복귀 순간 뱀이 순간이동하지 않게
      last = now;
      acc = 0;
      h.draw(0);
      raf = requestAnimationFrame(frame);
      return;
    }
    const tick = h.tickMs();
    const out = ticksToRun({
      accMs: acc,
      deltaMs: now - last,
      tickMs: tick,
      maxFrameDeltaMs: R.maxFrameDeltaMs,
      maxCatchupTicks: R.maxCatchupTicks,
    });
    last = now;
    acc = out.accMs;
    for (let i = 0; i < out.ticks && !h.done(); i++) h.advance();
    const steps = Math.max(1, h.alphaSteps?.() ?? R.alphaSteps);
    const raw = Math.min(1, acc / tick);
    h.draw(Math.round(raw * steps) / steps);
    raf = requestAnimationFrame(frame);
  };
  raf = requestAnimationFrame(frame);

  return {
    stop() {
      stopped = true;
      cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVisibility);
    },
  };
}
